import SearchOutlined from '@ant-design/icons/SearchOutlined'
import { useDebounce } from '@uidotdev/usehooks'
import { Modal } from 'antd'
import axios, { AxiosError } from 'axios'
import classNames from 'classnames'
import { Suspense, useContext, useEffect, useRef, useState } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { toast } from 'sonner'
import { ZodError } from 'zod'

import Image from '@/components/Atoms/Image/Image'
import Loader from '@/components/Atoms/Loader/Loader'
import OrganisationBar from '@/components/OrganisationBar/OrganisationBar'
import { DELAY, GENERAL_ERRORS_MESSAGES } from '@/constants'
import useSearch from '@/hooks/search/useSearch'
import { usePageScrolled } from '@/hooks/ui/usePageScrolled'
import { ScreenSizeContext } from '@/providers/ScreenSizeProvider'
import { categorySchema } from '@/schemas'
import { useFeedStore } from '@/stores/feed/feedStore'
import { useTreeTilesStore } from '@/stores/feed/treeTilesStore'
import { useUserInfoStore } from '@/stores/user/userInfoStore'
import api from '@/utils/api/api'
import getGeneralErrorMessage from '@/utils/api/errors/getGeneralErrorMessage'
import { safeLazy } from '@/utils/app/safeLazy'
import { isNomenclatureEditor } from '@/utils/auth/userRoles'
import { filterApprovedCategoryTree } from '@/utils/catalog/approvalStatus'
import { cleanCategoryTree } from '@/utils/catalog/cleanDescription'
import { getCarriedCharacteristics } from '@/utils/search/characteristics/carriedCharacteristics'

import {
  isSuggestionsQuerySettled,
  normalizeSuggestionsQuery,
  shouldCloseSuggestionsOnScroll,
} from './lib/mainSuggestions'
import { SEARCH_TITS_DEFAULT_LENGTH } from './Main.config'
import cn from './Main.module.scss'
import {
  NomenclatureSuggestion,
  SuggestionItem,
  SuggestionsResponse,
} from './Main.types'

const TreeTiles = safeLazy(() => import('@/components/TreeTiles/TreeTiles'))

const SUGGESTIONS_FADE_DURATION = 150
const getSuggestionsCacheKey = (query: string, clientId?: string) =>
  `${clientId ?? ''}:${query}`
const renderHighlightedText = (
  text: string,
  highlightedText?: string | null
) => {
  if (!highlightedText) return text

  const nodes = new DOMParser().parseFromString(highlightedText, 'text/html')
    .body.childNodes

  return Array.from(nodes).map((node, index) =>
    node.nodeName === 'EM' ? (
      <strong key={index}>{node.textContent}</strong>
    ) : (
      node.textContent
    )
  )
}

function Main() {
  const [categoriesTreeLoading, setCategoriesTreeLoading] =
    useState<boolean>(false)
  const [loadingError, setLoadingError] = useState<string>('')
  const [searchValue, setSearchValue] = useState<string>('')
  const [dogImageLink, setDogImageLink] = useState<string>('')
  const [isEmptyError, setIsEmptyError] = useState(false)
  const [isOrganisationInactive, setIsOrganisationInactive] =
    useState<boolean>(false)
  const [fatalError, setFatalError] = useState(false)
  const [articleTooltip, setArticleTooltip] = useState<number>(-1)
  const [copyNotification, setCopyNotification] = useState<number>(-1)

  const inputRef = useRef<HTMLInputElement | null>(null)
  const suggestionsDropdownRef = useRef<HTMLDivElement | null>(null)
  const suggestionsCacheRef = useRef(new Map<string, SuggestionsResponse>())
  const pendingSuggestionsQueriesRef = useRef(new Set<string>())
  const currentSuggestionsQueryRef = useRef('')

  const [suggestionsResponse, setSuggestionsResponse] =
    useState<SuggestionsResponse | null>(null)
  const [suggestionsLoading, setSuggestionsLoading] = useState<boolean>(false)

  const [searchInputFocused, setSearchInputFocused] = useState<boolean>(false)
  const [suggestionsUnlocked, setSuggestionsUnlocked] = useState<boolean>(false)
  const [showSuggestions, setShowSuggestions] = useState<boolean>(false)
  const [suggestionsMounted, setSuggestionsMounted] = useState<boolean>(false)
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] =
    useState<number>(-1)

  const [selectionMethod, setSelectionMethod] = useState<
    'keyboard' | 'mouse' | null
  >(null)

  const debouncedSearchValue = useDebounce(searchValue, DELAY)

  const { setProductsLoading } = useFeedStore((state) => state)

  const [, contextHolder] = Modal.useModal()

  const { categoriesTree, setCategoriesTree, categoriesPrioritiesVersion } =
    useTreeTilesStore((state) => state)
  const prevPrioritiesVersionRef = useRef(0)

  const { userId, selectedOrganisation, currentUser, currentClient } =
    useUserInfoStore((state) => state)
  const isEditor = useUserInfoStore((state) =>
    isNomenclatureEditor(state.tokenRoles)
  )

  const isOrganisationSelected =
    Object.keys(selectedOrganisation || {}).length > 0

  const screen = useContext(ScreenSizeContext)
  const isDesktop = [screen?.lg, screen?.xl].some(Boolean)
  const isPageScrolled = usePageScrolled()
  const search = useSearch()
  const [filterActionsSlot, setFilterActionsSlot] =
    useState<HTMLDivElement | null>(null)
  const [priorityFiltersSlot, setPriorityFiltersSlot] =
    useState<HTMLDivElement | null>(null)
  const location = useLocation()

  useEffect(() => {
    if (fatalError) return

    if (currentUser?.organisationShortModel?.isLegal === false) {
      currentSuggestionsQueryRef.current = ''
      setSuggestionsResponse(null)
      setShowSuggestions(false)
      return
    }

    const isSuggestionsPage =
      location.pathname === '/' || location.pathname.startsWith('/feed')

    if (!isSuggestionsPage) {
      currentSuggestionsQueryRef.current = ''
      setSuggestionsResponse(null)
      return
    }

    if (!isSuggestionsQuerySettled(searchValue, debouncedSearchValue)) return

    const query = normalizeSuggestionsQuery(debouncedSearchValue)

    if (!query) {
      currentSuggestionsQueryRef.current = ''
      setSuggestionsResponse(null)
      setShowSuggestions(false)
      setSuggestionsLoading(false)
      setSelectedSuggestionIndex(-1)
      return
    }

    if (!searchInputFocused || !suggestionsUnlocked) {
      setShowSuggestions(false)
      return
    }

    if (document.visibilityState !== 'visible') {
      return
    }

    const cacheKey = getSuggestionsCacheKey(query, currentClient?.id)
    currentSuggestionsQueryRef.current = cacheKey
    const cachedSuggestions = suggestionsCacheRef.current.get(cacheKey)
    if (cachedSuggestions) {
      setSuggestionsResponse(cachedSuggestions)
      setShowSuggestions(true)
      setSuggestionsLoading(false)
      return
    }

    if (pendingSuggestionsQueriesRef.current.has(cacheKey)) {
      return
    }

    pendingSuggestionsQueriesRef.current.add(cacheKey)
    setSuggestionsLoading(true)

    api
      .get('/nomenclatures/suggestions', {
        params: {
          query,
          limit: SEARCH_TITS_DEFAULT_LENGTH,
          withLeftovers: false,
          ...(currentClient?.id && { client: currentClient.id }),
        },
        useTimeout: false,
      })
      .then((response) => {
        const data: SuggestionsResponse = response.data
        suggestionsCacheRef.current.set(cacheKey, data)
        if (
          currentSuggestionsQueryRef.current === cacheKey &&
          searchInputFocused &&
          document.visibilityState === 'visible'
        ) {
          setSuggestionsResponse(data)
          setShowSuggestions(true)
          setSelectedSuggestionIndex(-1)
        }
      })
      .catch((error) => {
        if (currentSuggestionsQueryRef.current !== cacheKey) return

        setSuggestionsResponse(null)

        if (
          !(error instanceof AxiosError && (error.response?.status ?? 0) >= 500)
        ) {
          setShowSuggestions(true)
        }

        if (
          error instanceof AxiosError &&
          (error.response?.status ?? 0) >= 500
        ) {
          setFatalError(true)
          setShowSuggestions(false)
        }
      })
      .finally(() => {
        pendingSuggestionsQueriesRef.current.delete(cacheKey)
        if (currentSuggestionsQueryRef.current === cacheKey) {
          setSuggestionsLoading(false)
        }
      })
  }, [
    searchValue,
    debouncedSearchValue,
    searchInputFocused,
    suggestionsUnlocked,
    location.pathname,
    currentClient,
    fatalError,
  ])

  useEffect(() => {
    if (!showSuggestions) return

    const handleScroll = (event: Event) => {
      if (
        !shouldCloseSuggestionsOnScroll(
          event.target,
          inputRef.current,
          suggestionsDropdownRef.current
        )
      ) {
        return
      }
      setShowSuggestions(false)
      setSuggestionsUnlocked(false)
      setSelectedSuggestionIndex(-1)
    }

    window.addEventListener('scroll', handleScroll, {
      capture: true,
      passive: true,
    })

    return () => {
      window.removeEventListener('scroll', handleScroll, true)
    }
  }, [showSuggestions])

  useEffect(() => {
    if (showSuggestions) {
      setSuggestionsMounted(true)
      return
    }

    if (!suggestionsMounted) return

    const timeout = setTimeout(() => {
      setSuggestionsMounted(false)
    }, SUGGESTIONS_FADE_DURATION)

    return () => clearTimeout(timeout)
  }, [showSuggestions, suggestionsMounted])

  useEffect(() => {
    suggestionsCacheRef.current.clear()
    if (location.pathname === '/') {
      setSearchValue('')
      currentSuggestionsQueryRef.current = ''
      setSuggestionsResponse(null)
      setShowSuggestions(false)
      setSelectedSuggestionIndex(-1)
    }
  }, [location.pathname])

  useEffect(() => {
    if (isEditor) {
      const getDogImageLink = () => {
        axios
          .get('https://dog.ceo/api/breeds/image/random')
          .then((response) => {
            setDogImageLink(response.data.message)
          })
      }

      getDogImageLink()

      const dogImageRequestInterval = setInterval(() => {
        getDogImageLink()
      }, DELAY * 10)

      return () => {
        clearInterval(dogImageRequestInterval)
      }
    }
  }, [isEditor])

  useEffect(() => {
    const versionChanged =
      categoriesPrioritiesVersion !== prevPrioritiesVersionRef.current
    const shouldFetch =
      (versionChanged || !categoriesTree.length) &&
      localStorage.getItem('refresh-token') &&
      userId &&
      !isEditor

    if (!shouldFetch) return

    prevPrioritiesVersionRef.current = categoriesPrioritiesVersion
    setCategoriesTreeLoading(true)
    api
      .get('/categories/root-trees')
      .then((response) => {
        const treeData = response.data.trees || []
        const validatedCategories = categorySchema.array().parse(treeData)
        const cleanedData = cleanCategoryTree(
          filterApprovedCategoryTree(validatedCategories)
        )
        setCategoriesTree(cleanedData)
      })
      .catch((error) => {
        if (error instanceof AxiosError) {
          setLoadingError(getGeneralErrorMessage(error))
        }
        if (error instanceof ZodError) {
          console.error('Zod Error Parsing Categories:', error)
          setLoadingError(GENERAL_ERRORS_MESSAGES.ZOD_ERROR)
        }
      })
      .finally(() => {
        setCategoriesTreeLoading(false)
      })
  }, [userId, isEditor, categoriesTree.length, categoriesPrioritiesVersion])

  const handleSearchProducts = (query?: string) => {
    suggestionsCacheRef.current.clear()
    setSearchInputFocused(false)
    setShowSuggestions(false)
    setSuggestionsResponse(null)
    setSuggestionsUnlocked(false)
    setSelectedSuggestionIndex(-1)
    inputRef.current?.blur()

    const searchQuery = query || searchValue
    const trimmedSearchValue = searchQuery.trim().replace(/\s+/g, ' ')

    if (!trimmedSearchValue) {
      setIsEmptyError(true)
      setTimeout(() => {
        setIsEmptyError(false)
      }, 3000)
      return
    }

    if (currentUser?.organisationShortModel?.isLegal === false) {
      toast.warning('Организация не активирована', {
        description: 'Пожалуйста, обратитесь к своему менеджеру для активации',
      })
      return
    }

    setIsEmptyError(false)
    setShowSuggestions(false)
    setSuggestionsResponse(null)

    setProductsLoading(true)
    search({
      query: trimmedSearchValue,
      productCodes: [],
      categories: [],
      brands: [],
      characteristicFilters: getCarriedCharacteristics(),
      stores: [],
      page: 0,
      withLeftovers: false,
      resetFilters: true,
      triggerNavigation: true,
      isInitial: true,
    })
  }

  const handleCopyArticle = (article: string, index: number) => {
    navigator.clipboard.writeText(article).then(() => {
      setCopyNotification(index)
      setTimeout(() => {
        setCopyNotification(-1)
      }, 1500)
    })
  }

  const handleSuggestionClick = (item: SuggestionItem) => {
    suggestionsCacheRef.current.clear()
    setShowSuggestions(false)
    setSuggestionsResponse(null)
    setSearchInputFocused(false)
    setSuggestionsUnlocked(false)
    inputRef.current?.blur()

    setSearchValue('')
    currentSuggestionsQueryRef.current = ''

    const nomenclatureItem = item as NomenclatureSuggestion

    if ('article' in item) {
      search({
        query: '',
        productCodes: [nomenclatureItem.code],
        categories: nomenclatureItem.categoryId
          ? [{ id: nomenclatureItem.categoryId, description: '', count: 0 }]
          : [],
        brands: [],
        page: 0,
        characteristicFilters: getCarriedCharacteristics(),
        withLeftovers: false,
        resetFilters: true,
        triggerNavigation: true,
        isInitial: true,
      })
    } else if ('parentId' in item) {
      search({
        query: '',
        productCodes: [],
        categories: [
          {
            id: item.id,
            description: item.description,
            count: 0,
          },
        ],
        brands: [],
        page: 0,
        characteristicFilters: getCarriedCharacteristics(),
        withLeftovers: false,
        resetFilters: false,
        triggerNavigation: true,
        isInitial: true,
      })
    } else if ('countryId' in item) {
      search({
        query: '',
        categories: [],
        productCodes: [],
        brands: [
          {
            id: item.id,
            description: item.description,
            count: 0,
          },
        ],
        page: 0,
        characteristicFilters: getCarriedCharacteristics(),
        withLeftovers: false,
        resetFilters: true,
        triggerNavigation: true,
        isInitial: true,
      })
    }
  }

  const getAllItems = (): SuggestionItem[] => {
    if (!suggestionsResponse) return []
    return suggestionsResponse.groups.flatMap((group) => group.items)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const allItems = getAllItems()

    if (!showSuggestions || allItems.length === 0) {
      if (e.key === 'Enter') {
        handleSearchProducts()
      }
      if (e.key === 'Escape') {
        setShowSuggestions(false)
        setSuggestionsResponse(null)
        setSelectedSuggestionIndex(-1)
        setSelectionMethod(null)
      }
      return
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setSelectionMethod('keyboard')
        setSelectedSuggestionIndex((prev) =>
          prev < allItems.length - 1 ? prev + 1 : prev
        )
        break

      case 'ArrowUp':
        e.preventDefault()
        setSelectionMethod('keyboard')
        setSelectedSuggestionIndex((prev) => (prev > 0 ? prev - 1 : -1))
        break

      case 'Enter':
        e.preventDefault()
        if (selectedSuggestionIndex >= 0 && selectionMethod === 'keyboard') {
          handleSuggestionClick(allItems[selectedSuggestionIndex])
        } else {
          handleSearchProducts()
        }
        break

      case 'Escape':
        setShowSuggestions(false)
        setSuggestionsResponse(null)
        setSelectedSuggestionIndex(-1)
        setSelectionMethod(null)
        break
    }
  }

  const getGroupTitle = (groupType: string): string => {
    switch (groupType) {
      case 'CATEGORY':
        return 'Категории'
      case 'NOMENCLATURE':
        return 'Товары'
      case 'BRAND':
        return 'Бренды'
      default:
        return groupType
    }
  }

  const isNothingFound =
    showSuggestions &&
    !suggestionsLoading &&
    (!suggestionsResponse ||
      suggestionsResponse.groups.every((group) => group.items.length === 0))

  if (!userId) return null

  return (
    <section className={cn.main}>
      {isEditor && (
        <div className={cn.nomenclatureEditorGreeting}>
          <div className={cn.nomenclatureEditorGreetingInner}>
            <span>Гав-гав!🐶</span>
            <span>Добрый день, Галина!</span>
            <p>
              Здесь будут появляться случайные фотографии собачек. Приходите на
              эту страницу, если заработаетесь и станет грустно!
            </p>
            {dogImageLink && (
              <div>
                <img loading="lazy" src={dogImageLink} />
              </div>
            )}
          </div>
        </div>
      )}
      {!isEditor && (
        <>
          <div className={cn.searchBarContainer}>
            <div
              className={
                Object.keys(selectedOrganisation).length
                  ? [cn.searchBar, cn.organisationSelected].join(' ')
                  : cn.searchBar
              }
            >
              <div
                className={[
                  cn.searchBarInner,
                  isOrganisationSelected ? cn.searchBarInnerOrgSelected : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                <div className={cn.productsSearch}>
                  <input
                    ref={inputRef}
                    data-product-search-input
                    className={
                      isEmptyError || isOrganisationInactive
                        ? cn.inputError
                        : ''
                    }
                    placeholder="next 5"
                    value={searchValue}
                    onMouseDown={() => {
                      setSuggestionsUnlocked(true)
                    }}
                    onFocus={() => {
                      setSearchInputFocused(true)

                      const input = inputRef.current
                      if (input) {
                        const { length } = input.value
                        requestAnimationFrame(() => {
                          input.setSelectionRange(length, length)
                        })
                      }
                    }}
                    onBlur={(e) => {
                      setSearchInputFocused(false)
                      setSuggestionsUnlocked(false)

                      const nextTarget = e.relatedTarget as HTMLElement | null

                      const shouldHide =
                        !nextTarget ||
                        !e.currentTarget.parentElement?.contains(nextTarget)

                      if (shouldHide) {
                        setShowSuggestions(false)
                        setSelectedSuggestionIndex(-1)
                      }
                    }}
                    onChange={(e) => {
                      const value = e.target.value
                      const query = normalizeSuggestionsQuery(value)

                      setSuggestionsUnlocked(true)
                      setSearchValue(value)
                      if (!query) {
                        currentSuggestionsQueryRef.current = ''
                        setSuggestionsResponse(null)
                        setSuggestionsLoading(false)
                        setShowSuggestions(false)
                        setSelectedSuggestionIndex(-1)
                        setSelectionMethod(null)
                        return
                      }

                      const cacheKey = getSuggestionsCacheKey(
                        query,
                        currentClient?.id
                      )
                      const cachedSuggestions =
                        suggestionsCacheRef.current.get(cacheKey)

                      currentSuggestionsQueryRef.current = cacheKey
                      setSuggestionsResponse(cachedSuggestions ?? null)
                      setSuggestionsLoading(
                        Boolean(query) && !cachedSuggestions
                      )
                      setShowSuggestions(true)
                      if (isEmptyError) setIsEmptyError(false)
                      if (isOrganisationInactive)
                        setIsOrganisationInactive(false)

                      setSelectedSuggestionIndex(-1)
                      setSelectionMethod(null)
                    }}
                    onKeyDown={handleKeyDown}
                  />
                  {isEmptyError && (
                    <div className={cn.toastError}>Введите что-нибудь</div>
                  )}

                  {suggestionsMounted && (
                    <div
                      ref={suggestionsDropdownRef}
                      className={`${cn.suggestionsDropdown} ${
                        !isOrganisationSelected ? cn.orangeTheme : ''
                      } ${showSuggestions ? cn.suggestionsVisible : ''}`}
                      onMouseLeave={() => setSelectedSuggestionIndex(-1)}
                    >
                      {suggestionsLoading ? (
                        <div className={cn.nothingFound}>
                          <Loader size={20} />
                        </div>
                      ) : isNothingFound ? (
                        <div className={cn.nothingFound}>Ничего не найдено</div>
                      ) : (
                        suggestionsResponse?.groups.map((group, groupIndex) => {
                          if (group.items.length === 0) return null

                          const groupStartIndex = suggestionsResponse.groups
                            .slice(0, groupIndex)
                            .reduce((sum, g) => sum + g.items.length, 0)

                          return (
                            <div
                              key={group.group_type}
                              className={cn.suggestionGroup}
                            >
                              <div className={cn.suggestionGroupTitle}>
                                {getGroupTitle(group.group_type)}
                              </div>

                              {group.items.map((item, itemIndex) => {
                                const flatIndex = groupStartIndex + itemIndex
                                const isNomenclature = 'article' in item
                                const nomenclatureItem =
                                  item as NomenclatureSuggestion
                                const isInStock = isNomenclature
                                  ? nomenclatureItem.inStock
                                  : true
                                const article = isNomenclature
                                  ? (item as NomenclatureSuggestion).article ||
                                    '-'
                                  : undefined

                                return (
                                  <div
                                    key={item.id}
                                    className={
                                      flatIndex === selectedSuggestionIndex
                                        ? `${cn.suggestionItem} ${cn.suggestionItemActive}`
                                        : cn.suggestionItem
                                    }
                                    onMouseDown={(e) => {
                                      const target = e.target as HTMLElement
                                      if (
                                        !target.classList.contains(
                                          cn.suggestionArticle
                                        )
                                      ) {
                                        e.preventDefault()
                                        handleSuggestionClick(item)
                                      }
                                    }}
                                    onMouseEnter={() => {
                                      setSelectedSuggestionIndex(flatIndex)
                                      setSelectionMethod('mouse')
                                    }}
                                  >
                                    {isNomenclature && (
                                      <div className={cn.suggestionImage}>
                                        <Image
                                          productCode={
                                            (item as NomenclatureSuggestion)
                                              .code
                                          }
                                        />
                                      </div>
                                    )}

                                    <div className={cn.suggestionContent}>
                                      {isNomenclature && (
                                        <div className={cn.articleContainer}>
                                          <span
                                            className={`${cn.suggestionArticle} ${article === '-' ? cn.suggestionArticleEmpty : ''}`}
                                            onMouseEnter={() => {
                                              if (article !== '-') {
                                                setArticleTooltip(flatIndex)
                                              }
                                            }}
                                            onMouseLeave={() =>
                                              setArticleTooltip(-1)
                                            }
                                            onMouseDown={(e) => {
                                              e.stopPropagation()
                                              e.preventDefault()
                                              if (article && article !== '-') {
                                                handleCopyArticle(
                                                  article,
                                                  flatIndex
                                                )
                                              }
                                            }}
                                          >
                                            Арт:{' '}
                                            {article === '-'
                                              ? '-'
                                              : renderHighlightedText(
                                                  article!,
                                                  nomenclatureItem.highlightedArticle
                                                )}
                                            {articleTooltip === flatIndex && (
                                              <span
                                                className={cn.articleTooltip}
                                              >
                                                копировать
                                              </span>
                                            )}
                                          </span>

                                          {isInStock === false && (
                                            <span className={cn.noStockLabel}>
                                              нет на складе
                                            </span>
                                          )}

                                          {copyNotification === flatIndex && (
                                            <div
                                              className={cn.copyNotification}
                                            >
                                              скопировано
                                            </div>
                                          )}
                                        </div>
                                      )}

                                      <span
                                        className={cn.suggestionText}
                                        title={item.description}
                                      >
                                        {renderHighlightedText(
                                          item.description,
                                          item.highlightedDescription
                                        )}
                                      </span>
                                    </div>
                                  </div>
                                )
                              })}
                            </div>
                          )
                        })
                      )}
                    </div>
                  )}

                  <button
                    type="button"
                    className={cn.searchButton}
                    aria-label="Найти"
                    onClick={() => handleSearchProducts()}
                  >
                    <SearchOutlined />
                  </button>
                </div>
                {isDesktop && (
                  <div
                    ref={setPriorityFiltersSlot}
                    className={classNames(
                      cn.priorityFiltersSlot,
                      isPageScrolled && cn.priorityFiltersSlotVisible
                    )}
                  />
                )}
                {isOrganisationSelected && screen?.md && (
                  <div className={cn.orgBarWrapper}>
                    <OrganisationBar variant="inline" />
                  </div>
                )}
                {isDesktop && (
                  <div
                    ref={setFilterActionsSlot}
                    className={cn.filterActionsSlot}
                  />
                )}
              </div>
            </div>
            {isOrganisationSelected &&
              [screen?.xs, screen?.sm].some((value) => value) && (
                <div className={cn.mobileOrgBarWrapper}>
                  <OrganisationBar variant="inline" />
                </div>
              )}
          </div>
          {location.pathname == '/' && (
            <Suspense fallback={null}>
              <TreeTiles
                tree={categoriesTree}
                treeLoading={categoriesTreeLoading}
                loadingError={loadingError}
                hideSearch={true}
              />
            </Suspense>
          )}
          {location.pathname == '/' &&
            loadingError &&
            !categoriesTreeLoading && (
              <div className={cn.errorMessage}>
                <span>{loadingError}</span>
              </div>
            )}
          {contextHolder}
          <Outlet context={{ filterActionsSlot, priorityFiltersSlot }} />
        </>
      )}
    </section>
  )
}

export default Main
